import type { InferResponseType } from 'hono/client';
import { useSWRConfig } from 'swr';
import { type ApiClient, useFetch, useMutation } from '../api';

type GetImageTag =
  ApiClient['v1']['workspaces'][':workspaceId']['registry']['repositories'][':repositoryId']['tags'][':imageTag']['$get'];
export type ImageTagDetailResponse = InferResponseType<GetImageTag, 200>;
export type ImageScanResult = NonNullable<ImageTagDetailResponse['scanResults']>;
export type ImageScanFinding = NonNullable<ImageScanResult['findings']>[number];
export type ImageTagApplication = ImageTagDetailResponse['applications'][number];
export type ImageIndexChild = NonNullable<ImageTagDetailResponse['children']>[number];

export const imageTagDetailKey = (workspaceId: number, repositoryId: number, imageTag: string) =>
  `workspaces/${workspaceId}/repositories/${repositoryId}/tags/${imageTag}`;

export function useImageTagDetail(workspaceId: number, repositoryId: number, imageTag: string) {
  return useFetch<ImageTagDetailResponse>(
    imageTagDetailKey(workspaceId, repositoryId, imageTag),
    (client) =>
      client.v1.workspaces[':workspaceId'].registry.repositories[':repositoryId'].tags[
        ':imageTag'
      ].$get({
        param: {
          workspaceId: workspaceId.toString(),
          repositoryId: repositoryId.toString(),
          imageTag,
        },
      })
  );
}

type VulnerabilityFindingMutationArgs = {
  applicationId: number;
  vulnerabilityId: string;
  packageName: string;
};

type DismissVulnerabilityFindingArgs = VulnerabilityFindingMutationArgs & {
  reason: string;
};

export function useDismissVulnerabilityFinding(
  workspaceId: number,
  repositoryId: number,
  imageTag: string
) {
  const { mutate } = useSWRConfig();
  const runMutation = useMutation<unknown, DismissVulnerabilityFindingArgs>(
    `workspaces/${workspaceId}/repositories/${repositoryId}/vulnerability-dismissals`,
    (client, arg) =>
      client.v2.workspaces[':workspaceId'].registry.repositories[':repositoryId'][
        'vulnerability-dismissals'
      ].$post({
        param: { workspaceId: workspaceId.toString(), repositoryId: repositoryId.toString() },
        json: {
          applicationId: arg.applicationId,
          vulnerabilityId: arg.vulnerabilityId,
          packageName: arg.packageName,
          reason: arg.reason,
        },
      })
  );

  return async (arg: DismissVulnerabilityFindingArgs) => {
    const result = await runMutation(arg);
    await mutate(imageTagDetailKey(workspaceId, repositoryId, imageTag));
    return result;
  };
}

export function useReopenVulnerabilityFinding(
  workspaceId: number,
  repositoryId: number,
  imageTag: string
) {
  const { mutate } = useSWRConfig();
  const runMutation = useMutation<unknown, VulnerabilityFindingMutationArgs>(
    `workspaces/${workspaceId}/repositories/${repositoryId}/vulnerability-dismissals/reopen`,
    (client, arg) =>
      client.v2.workspaces[':workspaceId'].registry.repositories[':repositoryId'][
        'vulnerability-dismissals'
      ].$delete({
        param: { workspaceId: workspaceId.toString(), repositoryId: repositoryId.toString() },
        query: {
          applicationId: arg.applicationId.toString(),
          vulnerabilityId: arg.vulnerabilityId,
          packageName: arg.packageName,
        },
      })
  );

  return async (arg: VulnerabilityFindingMutationArgs) => {
    const result = await runMutation(arg);
    await mutate(imageTagDetailKey(workspaceId, repositoryId, imageTag));
    return result;
  };
}
